<template>
  <BootstrapAlert v-if="fatalError" variant="danger">{{ fatalError }}</BootstrapAlert>
  <ProductFormPresentational
    v-model:description="description"
    v-model:name="name"
    v-model:product-category="productCategory"
    v-model:product-sub-category="productSubCategory"
    v-model:product-group="productGroup"
    v-model:product-type="productType"
    v-model:current-lifecycle-stage="currentLifecycleStage"
    v-model:current-lifecycle-start-date="currentLifecycleStartDate"
    v-model:current-lifecycle-end-date="currentLifecycleEndDate"
    v-model:regions="regions"
    v-model:sku-part="skuPart"
    :error-messages="errorMessages"
    :loading="loading"
    :server-error="serverError"
    :product-category-options="productCategoryOptions"
    :product-sub-category-options="productSubCategoryOptions"
    :product-group-options="productGroupOptions"
    :product-type-options="productTypeOptions"
    :regions-options="regionsOptions"
    @submit="submit"
  />
</template>
<script lang="ts" setup>
import {ProductCreateUpdate} from '@/stores/userManagement/userManagement';
import {Detail} from '@/api/errorMessage';

const props = defineProps<{
  isUpdateForm: boolean;
  pid: number;
  product?: Product;
}>();

const emit = defineEmits<{
  'update:products': [];
}>();

const router = useRouter();

// Stores
const productCategoriesStore = useProductCategoriesStore();
const ProductSubCategoriesStore = useProductSubCategoriesStore();
const productGroupsStore = useProductGroupsStore();
const productsStore = useProductsStore();
const productTypesStore = useProductTypesStore();

// Used to store errors that are never supposed to happen
const fatalError = ref<string | undefined>();
const fatal = (error: string): Error => {
  fatalError.value = error;
  return Error(`Fatal error: ${error}`);
};

// Store refs
const getProductCategories = computed(() => productCategoriesStore.getProductCategories);
const getProductSubCategories = computed(() => ProductSubCategoriesStore.getProductSubCategories);
const getProductGroups = computed(() => productGroupsStore.getProductGroups);
const getProductTypes = computed(() => productTypesStore.getProductTypes);

const loading = ref(false);

// Form data
const description = ref('');
const name = ref('');
const productCategory = ref<number | string>('');
const productSubCategory = ref<number | string>('');
const productGroup = ref<number | string>('');
const productType = ref('');
const currentLifecycleStage = ref('');
const currentLifecycleStartDate = ref('');
const currentLifecycleEndDate = ref('');
const regions = ref<string[]>([]);
const skuPart = ref('');
const errorMessages = ref(new Map());
// Used to tell the input not to use client-side validation
const serverError = ref(false);

// Select options
const productCategoryOptions = computed(
  () =>
    (getProductCategories.value.length > 0 &&
      getProductCategories.value.map(({name, id}) => ({label: name, value: id}))) ||
    [],
);
const productSubCategoryOptions = computed(() => {
  if (productCategory.value !== null) {
    const id = getProductCategories.value.find(({id}) => id === productCategory.value)?.id;
    return getProductSubCategories.value
      .filter(({category_id}) => category_id === id)
      .map(({name, id}) => ({label: name, value: id}));
  } else {
    return [];
  }
});
const productGroupOptions = computed(() => {
  if (productSubCategory.value !== null) {
    const id = getProductSubCategories.value.find(({id}) => id === productSubCategory.value)?.id;
    return getProductGroups.value
      .filter(({subcategory_id}) => subcategory_id === id)
      .map(({name, id}) => ({label: name, value: id}));
  } else {
    return [];
  }
});
const productTypeOptions = computed(
  () => (getProductTypes.value.length > 0 && getProductTypes.value.map(({name}) => name)) || [],
);
const regionsOptions = ref(['IE', 'NL', 'UK']);

const loadCategoriesFromQuery = async () => {
  const cid = Number(router.currentRoute.value.query.cid);
  const scid = Number(router.currentRoute.value.query.scid);
  const gid = Number(router.currentRoute.value.query.gid);

  if (cid !== undefined) {
    productCategory.value = getProductCategories.value.find(({id}) => id === cid)?.id || '';
  }
  if (scid !== undefined && productCategory.value !== null) {
    productSubCategory.value = getProductSubCategories.value.find(({id}) => id === scid)?.id || '';
  }
  if (gid !== undefined && productSubCategory.value !== null) {
    productGroup.value = getProductGroups.value.find(({id}) => id === gid)?.id || '';
  }
};

const submit = async (form: globalThis.Ref<HTMLFormElement | null>) => {
  try {
    const categoryId = getProductCategories.value.find(({id}) => id === productCategory.value)?.id;
    const subCategoryId = getProductSubCategories.value.find(
      ({id, category_id}) => id === productSubCategory.value && category_id === categoryId,
    )?.id;
    const groupId = getProductGroups.value.find(
      ({id, subcategory_id}) => id === productGroup.value && subcategory_id === subCategoryId,
    )?.id;
    const typeId = getProductTypes.value.find(({name}) => name === productType.value)?.id;

    if (!groupId) throw fatal('Missing product group');
    if (!typeId) throw fatal('Missing product type');

    loading.value = true;
    const product: ProductCreateUpdate = {
      product_group_id: groupId,
      product_type_id: typeId,
      sku_part: skuPart.value,
      name: name.value,
      description: description.value,
      managed: true,
      current_lifecycle_stage: currentLifecycleStage.value,
      current_lifecycle_start_date: currentLifecycleStartDate.value,
      current_lifecycle_end_date:
        currentLifecycleEndDate.value === '' ? null : currentLifecycleEndDate.value,
      region_ids: regions.value,
    };
    if (props.isUpdateForm === false) {
      const o = await productsStore.createProduct(product);
      if (o) emit('update:products');
      router.go(-1);
    } else {
      const updateProduct: ProductCreateUpdate = {
        ...product,
        id: props.pid,
      };
      const o = await productsStore.updateProduct(updateProduct);
      if (o) emit('update:products');
      router.go(-1);
    }
  } catch (errorMessage: any) {
    serverError.value = true;
    errorMessages.value = new Map();
    // Remove .was-validated for server-side validation response.
    form.value?.classList.remove('was-validated');
    if (typeof errorMessage !== 'string' && errorMessage.length > 0) {
      errorMessage.forEach((d: Detail) => errorMessages.value.set(d.loc[1], d.msg));
    }
    form.value?.classList.remove('was-validated');
    console.log(errorMessage);
  } finally {
    loading.value = false;
  }
};

const load = async () => {
  if (props.isUpdateForm === true && props.product !== undefined) {
    // Set the form data
    description.value = props.product.description;
    name.value = props.product.name;
    skuPart.value = props.product.sku_part;
    productType.value =
      getProductTypes.value.find(({id}) => id === props.product?.product_type_id)?.name || '';
    currentLifecycleStage.value = props.product.current_lifecycle_stage;
    currentLifecycleStartDate.value = props.product.current_lifecycle_start_date.slice(0, 10);
    currentLifecycleEndDate.value = props.product.current_lifecycle_end_date?.slice(0, 10) || '';
    regions.value = props.product.region_ids;
    // Get the group
    const foundProductGroup = getProductGroups.value.find(
      ({id}) => id === props.product?.product_group_id,
    );
    // Get the sub-category
    const foundProductSubCategory =
      foundProductGroup !== undefined
        ? getProductSubCategories.value.find(({id}) => id === foundProductGroup.subcategory_id)
        : undefined;
    // Get the category
    const foundProductCategory =
      foundProductSubCategory !== undefined
        ? getProductCategories.value.find(({id}) => id === foundProductSubCategory.category_id)
        : undefined;

    // Set the category dropdowns
    productGroup.value = foundProductGroup !== undefined ? foundProductGroup.id : '';
    productSubCategory.value =
      foundProductSubCategory !== undefined ? foundProductSubCategory.id : '';
    productCategory.value = foundProductCategory !== undefined ? foundProductCategory.id : '';
  } else {
    await loadCategoriesFromQuery();
  }
};

watch(productCategory, () => {
  const category = getProductCategories.value.find(({id}) => id === productCategory.value);
  if (category !== undefined) {
    const subCategory = getProductSubCategories.value.find(
      ({id}) => id === productSubCategory.value,
    );
    if (subCategory !== undefined) {
      if (subCategory.category_id !== category.id) {
        productSubCategory.value = '';
        productGroup.value = '';
      }
    }
  }
});

watch(productSubCategory, () => {
  const subCategory = getProductSubCategories.value.find(({id}) => id === productSubCategory.value);
  if (subCategory !== undefined) {
    const group = getProductGroups.value.find(({id}) => id === productGroup.value);
    if (group !== undefined) {
      if (group.subcategory_id !== subCategory.id) {
        productGroup.value = '';
      }
    }
  }
});

onMounted(() => load());
</script>
